import Carousel from 'react-multi-carousel';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { generatePositionTitle, isUserAllowedToApply } from 'shared-utils';
import CreateYourLevelUpAccountBanner from 'src/components/job/CreateYourLevelUpAccountBanner';
import HowLevelUpWorksCard from 'src/components/job/IndividualJob/HowLevelUpWorksCard';
import JobHeader from 'src/components/job/IndividualJob/JobHeader';
import JobDescription from 'src/components/job/postionDrawer/JobDescription';
import { PositionSkill } from 'src/components/job/postionDrawer/PositionDrawer';
import RightSection from 'src/components/job/postionDrawer/RightSection';
import Container from 'src/components/shared/Container';
import { useUserContext } from 'src/context/UserProvider';
import { useGetPositionDetailsByIdQuery } from 'src/hooks/position.hooks';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { ApplicationStatus, PositionRequirement, PositionTitle, UIPosition, UIUser } from 'types';
import { Loader } from 'ui';
import ShareablePositionCard from '../components/job/IndividualJob/ShareablePositionCard';
import { usePositionContext } from 'src/context/PositionProvider';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';

export const responsive = {
	superLargeDesktop: {
		breakpoint: { max: 4000, min: 3000 },
		items: 5,
	},
	desktop: {
		breakpoint: { max: 3000, min: 1220 },
		items: 3,
	},
	tablet: {
		breakpoint: { max: 1220, min: 764 },
		items: 2,
	},
	mobile: {
		breakpoint: { max: 764, min: 0 },
		items: 1,
	},
};

const Job = () => {
	const { jobId } = useParams();
	const [avatarImg, setAvatarImg] = useState<string>('');
	const [positionShouldBeUnlocked, setPositionShouldBeUnlocked] = useState<boolean>(false);
	const { user } = useUserContext();
	const { positions, applications } = usePositionContext();
	const [isUserAlreadyApplied, setIsUserAlreadyApplied] = useState<boolean | null>(null);
	const { data, isLoading } = useGetPositionDetailsByIdQuery(jobId, data => {
		const isUserApplied =
			data.applications?.some(
				application =>
					application.user._id === user?._id && application.status === ApplicationStatus.APPLIED_BY_JOBSEEKER,
			) ?? false;

		setIsUserAlreadyApplied(isUserApplied);
	});
	const positionTitle = generatePositionTitle(data?.position?.title as PositionTitle);

	const generateAvatar = useCallback(async () => {
		const res = await generateCompanyLogoAvatarSrc(
			data?.position?.company?.logo || '',
			data?.position?.company?.url || '',
		);
		setAvatarImg(res);
	}, [data?.position?.company?.logo, data?.position?.company?.url]);

	const positionSkills =
		data?.position?.positionRequirement.skillThresholds &&
		data.position?.positionRequirement.skillThresholds
			.filter(pst => pst.threshold > 0)
			.map(pst => {
				const skillRatings = user?.evaluation?.skillRatings?.filter(uSR =>
					pst.skills.map(s => String(s._id)).includes(String(uSR?.skill)),
				);
				return {
					id: pst.displaySkill,
					name: pst.displaySkill,
					threshold: pst.threshold,
					rating: skillRatings?.length
						? skillRatings?.reduce((rating, sr) => Math.max(rating, sr.rating || 0), 0) || 0
						: -1,
				};
			});

	const numberOfSkillsBelowThreshold = positionSkills?.filter(s => s.rating < s.threshold).length;

	useEffect(() => {
		generateAvatar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data?.position?.company?.logo, data?.position?.company?.url]);

	useEffect(() => {
		if (data) {
			const isUserApplied =
				data.applications?.some(
					application =>
						application.user._id === user?._id && application.status === ApplicationStatus.APPLIED_BY_JOBSEEKER,
				) ?? false;

			setIsUserAlreadyApplied(isUserApplied);
			const isUserAllowed = isUserAllowedToApply({
				isUserPlaced: user?.flags?.isPlaced || false,
				activeApplicantCount: positions.activeApplicantCount,
			});
			console.log('Debug: isUserAllowed', isUserAllowed, user?.flags?.isPlaced, positions.activeApplicantCount);
			const shouldBeUnlocked =
				(isUserAllowed &&
					data?.applications?.some(
						application =>
							application.user._id === user?._id &&
							[ApplicationStatus.OPENED_BY_EMPLOYER, ApplicationStatus.OPENED_BY_ADMIN].includes(application.status),
					)) ||
				data?.isAllowedToApply ||
				false;
			setPositionShouldBeUnlocked(shouldBeUnlocked);
		}
	}, [data, user?._id, user?.flags?.isPlaced, positions.activeApplicantCount]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center">
				{/* <Loader /> */}
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
			</div>
		);
	}

	return (
		<Container>
			{/* Banner for not signed in user's */}
			{!user ? (
				<div className="pt-4 mb-3">
					<CreateYourLevelUpAccountBanner />
				</div>
			) : null}
			<div className="py-2">
				<JobHeader
					positionDetail={data?.position as UIPosition}
					positionTitle={positionTitle}
					positionRecommendations={data?.recommendations}
					companyName={data?.position?.company.name || ''}
					postedOn={data?.position?.createdAt as Date}
					positionLevel={Math.round(data?.position?.positionRequirement.levelThreshold || 0)}
					companyLogo={avatarImg}
					images={[]}
					shouldBeUnlocked={positionShouldBeUnlocked}
					numberOfSkillsBelowThreshold={numberOfSkillsBelowThreshold || 0}
					isUserAlreadyApplied={isUserAlreadyApplied}
					userDetail={user as UIUser}
					subTextForReason={data?.reason}
				/>
			</div>
			<div className="py-4 border-t-4 flex flex-row gap-6 justify-between sm:flex-col">
				<div className="md:max-w-[70%] sm:w-full">
					<JobDescription
						workingMode={data?.position?.workingMode || ''}
						workingExp={
							data?.position?.positionRequirement.workExperience
								? `${Math.floor(data?.position?.positionRequirement.workExperience.min)} Yrs ${
										Math.ceil(data?.position?.positionRequirement.workExperience?.min * 12) % 12
								  } Mon - ${Math.floor(data?.position?.positionRequirement.workExperience.max)} Yrs ${
										Math.ceil(data?.position?.positionRequirement.workExperience?.max * 12) % 12
								  } Mon `
								: ''
						}
						positionRequirenment={data?.position?.positionRequirement as PositionRequirement}
						minSalary={data?.position?.salary.min || 0}
						maxSalary={data?.position?.salary.max || 0}
						location={data?.position?.workLocation || ''}
						jobType={data?.position?.positionType || ''}
						description={data?.position?.description || ''}
						minInternshipSalary={data?.position?.internshipSalary?.min}
						maxInternshipSalary={data?.position?.internshipSalary?.max}
						internshipDuration={data?.position?.internshipDuration}
						bondDetails={data?.position?.bondDetails}
					/>
				</div>
				{!user ? (
					<div className="sm:w-full md:max-w-[30%]">
						<HowLevelUpWorksCard />
					</div>
				) : (
					<div className="ml-[66px] w-[420px] sm:ml-0 sm:mt-6 sm:w-full">
						<RightSection
							positionSkills={positionSkills as PositionSkill[]}
							position={data?.position as UIPosition}
							userDetail={user as UIUser}
						/>
					</div>
				)}
			</div>
			<div>
				<div>
					<div className="text-lg	dark:text-[#CCDDFF] text-[#111E2C] py-2 border-b-2 border-[#EBEBF0] dark:border-[#555770] font-medium">
						Similar Jobs
					</div>
					<div className="w-full my-4 sm:mb-12">
						<Carousel responsive={responsive}>
							{data?.positions?.map((elem, ind) => {
								const isAlreadyApplied = applications?.some(
									application =>
										application.user._id === user?._id &&
										String(application.position) === elem._id &&
										application.status === ApplicationStatus.APPLIED_BY_JOBSEEKER,
								);
								return (
									<ShareablePositionCard
										key={ind}
										data={elem}
										userDetail={user as UIUser}
										isUserEligible={
											positions.eligiblePositions?.map(p => String(p._id)).includes(String(elem._id)) ?? false
										}
										isAlreadyApplied={isAlreadyApplied}
									/>
								);
							})}
						</Carousel>
					</div>
				</div>
			</div>
		</Container>
	);
};

export default Job;
